// Body of the tempurpedic ProductRegistrationPage FORM state: the hero
// product_callout stream, the four-button product-type picker, and the
// hand-rolled registration form.
//
// `selectedType` is React state that drives all five concerns from one
// place: toggling the four product-detail sections' aria-hidden, moving the
// `required` attr / `form__field--required` marker to the selected type's
// fields, highlighting the picker button, and syncing the hidden
// selected_product_type input. The server mirror (RegistrationForm.__init__
// requiring the selected type's fields from the POSTed selected_product_type)
// must enforce the same field groups.
//
// The text/select fields render through the shared FormInput/FormSelect
// components, fed the same props `initFormFields` derives from the
// `.form-field-placeholder` data-attrs -- so the enhanced DOM is preserved
// while React owns it. The placeholder divs themselves are omitted: they are
// only a mount selector, and emitting them risks a widget doing
// createRoot-over React-owned nodes. Their one non-mount consumer, the
// tempurca `[data-name="base_serial"]` CSS hook, rides on the replacement
// .form-field-wrapper divs, which the tempurca row layout rules already
// target. The preferred_lang radio list and the newsletter checkbox are
// hand-rolled (never placeholder-mounted).
//
// All translatable copy arrives pre-localized as string props (server-side
// gettext; fr_CA is live on tempurca -- React `t` has no runtime locale).
import { Immutable } from "immer";
import React from "react";
import { FormErrorList } from "tsi-common-react/src/forms/FormErrorList/index";
import { FormInput } from "tsi-common-react/src/forms/FormInput/index";
import { FormSelect } from "tsi-common-react/src/forms/FormSelect/index";
import {
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Template as ProductCalloutBlock } from "../../templates/ProductCalloutBlockTemplate";

type IProps = Immutable<templates.ProductRegistrationPageTemplate>;
type IField = IProps["fields"][number];

const PRODUCT_TYPES = [
    "mattresses",
    "bed_bases",
    "accessories",
    "pillows",
] as const;
type ProductType = (typeof PRODUCT_TYPES)[number];

// The per-type field sets that get `required` toggled on; must match
// RegistrationForm.__init__'s field_map server-side.
const GROUP_FIELDS: Record<ProductType, readonly string[]> = {
    mattresses: ["mattress_collection", "mattress_size"],
    bed_bases: ["base_model", "base_size", "base_serial"],
    accessories: ["accessories_model", "accessories_size"],
    pillows: ["pillow_model", "pillow_size"],
};

const asProductType = (value: string): ProductType =>
    (PRODUCT_TYPES as readonly string[]).includes(value)
        ? (value as ProductType)
        : "mattresses";

const RegistrationField = (props: {
    field: IField;
    label: string;
    errors: readonly string[] | undefined;
    required: boolean;
    // Renders a literal `required` attr on only the selected product type's
    // fields; static fields never carry it.
    requiredAttr?: boolean;
}): React.JSX.Element => {
    const { field, label } = props;
    const common = {
        id: field.field_id,
        name: field.name,
        label: label,
        labelPlacement: "outside" as const,
        placeholder: label,
        validation: props.required ? (["required"] as const) : undefined,
        required: props.requiredAttr || undefined,
        errors: props.errors ?? [],
    };
    if (field.kind === "select") {
        const selectedValue = field.choices.find((c) => c.selected)?.value;
        return (
            <FormSelect
                {...common}
                type="choice"
                defaultValue={selectedValue}
                choices={field.choices.map((c) => ({
                    value: c.value,
                    label: c.label,
                }))}
            />
        );
    }
    const inputType = field.input_type === "email" ? "email" : "text";
    return (
        <FormInput
            {...common}
            type={inputType}
            autoComplete={inputType}
            maxLength={field.maxlength ? Number(field.maxlength) : undefined}
            defaultValue={field.value}
        />
    );
};

// The picker + form, extracted so the toggling state is unit-testable
// without the PageSkeleton document shell.
export const ProductRegistrationForm = (
    props: Pick<
        IProps,
        | "picker_items"
        | "copy"
        | "fields"
        | "field_labels"
        | "field_errors"
        | "non_field_errors"
        | "selected_product_type"
    >,
): React.JSX.Element => {
    const [selectedType, setSelectedType] = React.useState<ProductType>(() =>
        asProductType(props.selected_product_type),
    );

    const fieldsByName = new Map(props.fields.map((f) => [f.name, f]));
    const selectedFields = GROUP_FIELDS[selectedType];

    const renderField = (name: string): React.JSX.Element | null => {
        const field = fieldsByName.get(name);
        if (!field) {
            return null;
        }
        const isGroupField = PRODUCT_TYPES.some((t) =>
            GROUP_FIELDS[t].includes(name),
        );
        const isSelected = selectedFields.includes(name);
        return (
            <div className="form-field-wrapper" data-name={name} key={name}>
                <RegistrationField
                    field={field}
                    label={props.field_labels[name] ?? ""}
                    errors={props.field_errors[name]}
                    required={isGroupField ? isSelected : field.required}
                    requiredAttr={isGroupField && isSelected}
                />
            </div>
        );
    };

    const preferredLang = fieldsByName.get("preferred_lang");
    const newsletter = fieldsByName.get("newsletter");
    // The generated index-signature type reads as always-defined, but a
    // missing key is undefined at runtime -- the annotated return type keeps
    // the guard below honest (an unguarded .length would crash the SSR).
    const lookupErrors = (name: string): readonly string[] | undefined =>
        props.field_errors[name];
    const preferredLangErrors = lookupErrors("preferred_lang");

    const groupSections: Array<{
        type: ProductType;
        className: string;
        heading: string;
    }> = [
        {
            type: "mattresses",
            className: "mattress-fields",
            heading: props.copy.mattress_details,
        },
        {
            type: "bed_bases",
            className: "bed-base-fields",
            heading: props.copy.base_details,
        },
        {
            type: "accessories",
            className: "accessories-fields",
            heading: props.copy.accessory_details,
        },
        {
            type: "pillows",
            className: "pillow-fields",
            heading: props.copy.pillow_details,
        },
    ];

    return (
        <>
            <section className="product-registration__product-selection l-capped-width">
                <h2>{props.copy.select_type_heading}</h2>
                <div className="product-registration__product-selection--list">
                    {props.picker_items.map((item) => (
                        <button
                            key={item.block_type}
                            type="button"
                            className={
                                selectedType === item.block_type
                                    ? "product-registration__product-selection--item product-registration__product-selection--item--selected"
                                    : "product-registration__product-selection--item"
                            }
                            data-product-type={item.block_type}
                            aria-label={item.label}
                            onClick={() =>
                                setSelectedType(asProductType(item.block_type))
                            }
                        >
                            <img
                                src={item.image_url}
                                alt={item.image_alt}
                                loading="lazy"
                                decoding="async"
                            />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </div>
            </section>

            <section className="product-registration__form-section l-capped-width">
                <form
                    method="post"
                    encType="multipart/form-data"
                    className="registration-form"
                    noValidate
                >
                    <CSRFToken />

                    {/* Hidden field tracking the selected product type
                            (RegistrationForm.__init__ keys the server-side
                            required-field mirror on it). */}
                    <input
                        type="hidden"
                        name="selected_product_type"
                        id="id_selected_product_type"
                        value={selectedType}
                    />

                    <p>{props.copy.intro_text}</p>

                    {props.non_field_errors.length > 0 && (
                        <div className={FORM_FIELD_CLASS_NAME}>
                            <FormErrorList errors={props.non_field_errors} />
                        </div>
                    )}

                    {/* Personal information rows (form.personal_field_names) */}
                    <div className="row">
                        {renderField("fname")}
                        {renderField("lname")}
                    </div>
                    <div className="row">
                        {renderField("address_1")}
                        {renderField("address_2")}
                    </div>
                    <div className="row">
                        {renderField("city")}
                        {renderField("state")}
                    </div>
                    <div className="row">
                        {renderField("postal_code")}
                        {renderField("phone")}
                    </div>
                    <div className="row">{renderField("email")}</div>

                    {/* Product-specific sections, toggled by the picker */}
                    {groupSections.map((section) =>
                        section.type === "bed_bases" ? (
                            <div
                                key={section.type}
                                className={section.className}
                                aria-hidden={
                                    selectedType !== section.type
                                        ? "true"
                                        : "false"
                                }
                            >
                                <h3>{section.heading}</h3>
                                <div className="row">
                                    {renderField("base_model")}
                                    {renderField("base_size")}
                                </div>
                                <div className="row no-gap">
                                    {renderField("base_serial")}
                                    <div className="form__help">
                                        {props.copy.base_serial_help}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                key={section.type}
                                className={section.className}
                                aria-hidden={
                                    selectedType !== section.type
                                        ? "true"
                                        : "false"
                                }
                            >
                                <h3>{section.heading}</h3>
                                <div className="row">
                                    {GROUP_FIELDS[section.type].map(
                                        renderField,
                                    )}
                                </div>
                            </div>
                        ),
                    )}

                    {/* Purchase date */}
                    <div className="row">
                        <div className="form-field">
                            <h3>{props.copy.purchase_date_heading}*</h3>
                            <div className="row-nowrap">
                                {renderField("purchased_month")}
                                {renderField("purchased_day")}
                                {renderField("purchased_year")}
                            </div>
                        </div>
                    </div>

                    {/* Retailer */}
                    <div className="row">{renderField("retailer")}</div>

                    {/* Preferred language (hand-rolled radio list) */}
                    <div className="row">
                        <div className="form-field-wrapper">
                            <div
                                className={concatClassNames([
                                    FORM_FIELD_CLASS_NAME,
                                    FORM_FIELD_REQUIRED_CLASS_NAME,
                                ])}
                            >
                                <label className={FORM_LABEL_CLASS_NAME}>
                                    {props.field_labels["preferred_lang"] ?? ""}
                                </label>
                                <ul className="radio-list">
                                    {(preferredLang?.choices ?? []).map(
                                        (choice) => (
                                            <li key={choice.value}>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="preferred_lang"
                                                        value={choice.value}
                                                        defaultChecked={
                                                            choice.selected ||
                                                            undefined
                                                        }
                                                        required
                                                    />
                                                    {choice.label}
                                                </label>
                                            </li>
                                        ),
                                    )}
                                </ul>
                                {preferredLangErrors &&
                                    preferredLangErrors.length > 0 && (
                                        <FormErrorList
                                            errors={preferredLangErrors}
                                        />
                                    )}
                            </div>
                        </div>
                    </div>

                    {/* Newsletter (hand-rolled checkbox) */}
                    <div className="row">
                        <div className={FORM_FIELD_CLASS_NAME}>
                            <label className={FORM_LABEL_CLASS_NAME}>
                                <input
                                    type="checkbox"
                                    name="newsletter"
                                    value="on"
                                    defaultChecked={
                                        newsletter?.checked || undefined
                                    }
                                />
                                {props.field_labels["newsletter"] ?? ""}
                            </label>
                        </div>
                    </div>

                    <div className="row">
                        <button type="submit" className="btn btn-primary">
                            {props.copy.register_label}
                        </button>
                    </div>

                    <div className="row">
                        <p
                            dangerouslySetInnerHTML={{
                                __html: props.copy.customer_service_html,
                            }}
                        />
                    </div>
                </form>
            </section>
        </>
    );
};

export const ProductRegistrationPageBody = (
    props: IProps,
): React.JSX.Element => {
    return (
        <div className="container product-registration l-full-width">
            <StreamField
                tagName={React.Fragment}
                value={props.page.hero.value}
                components={{
                    product_callout: (block) => (
                        <ProductCalloutBlock value={block.value} />
                    ),
                }}
            />
            <ProductRegistrationForm
                picker_items={props.picker_items}
                copy={props.copy}
                fields={props.fields}
                field_labels={props.field_labels}
                field_errors={props.field_errors}
                non_field_errors={props.non_field_errors}
                selected_product_type={props.selected_product_type}
            />
        </div>
    );
};
